import { isNil } from 'lodash'

import { GenericalKey, GenericalRecord } from '../types/utilities'

/**
 * 转换成数组格式
 *
 * @param value 值
 */
export const toArray = <T>(value?: T | T[] | readonly T[]) => {
  if (isNil(value)) {
    return []
  }
  // 使用 slice 处理 proxy 数组对象
  return Array.isArray(value) ? value.slice() : [value as T]
}

/**
 * 数组转成 map
 *
 * @param array 数组
 * @param key key 字段
 * @param keyGenerator key 生成器
 */
export const array2Map = <
  T extends Record<GenericalKey, any>,
  K extends keyof T
>(
  array: T[],
  key: K
) => {
  const result: Record<Exclude<T[K], undefined | null>, T> = {} as any

  array.forEach(item => {
    if (!isNil(item[key])) {
      result[item[key]] = item
    }
  })

  return result
}

/**
 * 树形数组转为一维数组
 *
 * @param data 数据源
 */
export const flattenArrayTree = <T extends GenericalRecord>(
  data: T[],
  childrenKey = 'children',
  deep = Number.MAX_SAFE_INTEGER
) => {
  const flatten = (array: T[]): T[] => {
    return array.reduce((a, b) => {
      a.push(b)
      if (b[childrenKey] && deep > 0) {
        deep--
        a.push(...flatten(b[childrenKey]))
      }
      return a
    }, [] as T[])
  }

  return flatten(data)
}

/**
 * 数组型树转 Map
 *
 * @param array 源数据
 */
export const arrayTree2Map = <
  T extends { [key: string]: any; id?: string; children?: T[] }
>(
  array: T[],
  keyName: keyof T = 'id'
) => {
  const result: Record<string, T> = {}
  const transfer = (data: T[], key: string) => {
    data.forEach(item => {
      result[item[key] as string] = item
      if (item.children) {
        transfer(item.children, key)
      }
    })
  }
  transfer(array, keyName.toString())
  return result
}

/**
 * 查找 keyValues 所对应的 item 集合
 *
 * @param arrayTree 树形数组
 * @param keyValues key 值集合
 * @param equalityFn  判断是否为目标项的函数
 * @returns  匹配集合
 */
export const findTreeMatches = <T extends { children?: T[] }, K>(
  arrayTree: T[],
  keyValues: K[],
  equalityFn: (a: T, b: K) => boolean,
  options?: {
    // 是否为唯一 key：如果为 true，当匹配到的项跟 keyValues 长度相同时，停止查找
    isUniqueKey?: boolean
  }
) => {
  const matches: T[] = []
  const flattenArrar = flattenArrayTree(arrayTree)

  for (const item of flattenArrar) {
    if (options?.isUniqueKey !== false && matches.length === keyValues.length) {
      break
    }
    for (const key of keyValues) {
      if (equalityFn(item, key)) {
        matches.push(item)
        break
      }
    }
  }

  return matches
}

/**
 * 在树形数组中查找 `equalityFn` 对应项所在的路径
 *
 * @param data 树形数组
 * @param equalityFn 是否为目标项的函数
 * @returns 路径数组
 */
export const findTreePaths = <T extends { children?: T[] }>(
  data: T[],
  equalityFn: (value: T) => boolean
) => {
  const matches: T[] = []
  let isFound = false
  const find = (arr: T[]) => {
    for (const item of arr) {
      if (isFound) {
        break
      }
      if (equalityFn(item)) {
        isFound = true
        matches.push(item)
        break
      }
      if ('children' in item && item.children?.length) {
        matches.push(item)
        find(item.children)
        if (!isFound) {
          matches.pop()
        }
      }
    }
  }
  find(data)
  return matches
}

/**
 * 数组去重
 * @param array 数组
 * @param isSame 比较两个项是否相等
 * @returns 唯一数组
 */
export const deduplicate = <T>(array: T[], isSame: (a: T, b: T) => boolean) => {
  const uniqueArray: T[] = []
  array.forEach(item => {
    if (!uniqueArray.some(uniqueItem => isSame(uniqueItem, item))) {
      uniqueArray.push(item)
    }
  })
  return uniqueArray
}
